/* OTel wiring (traces, logs, metrics) straight against the OpenTelemetry SDK —
 * this used to go through @ai21/telemetry, which pinned vulnerable OTel
 * versions. Each signal is only wired when its URL is set, so telemetry stays
 * a true no-op until VITE_TELEMETRY_BASE_URL is configured per environment. */

import { LogAttributes, Logger, SeverityNumber } from "@opentelemetry/api-logs";
import { getWebAutoInstrumentations } from "@opentelemetry/auto-instrumentations-web";
import { OTLPLogExporter } from "@opentelemetry/exporter-logs-otlp-http";
import { OTLPMetricExporter } from "@opentelemetry/exporter-metrics-otlp-http";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { resourceFromAttributes } from "@opentelemetry/resources";
import {
  BatchLogRecordProcessor,
  LoggerProvider,
} from "@opentelemetry/sdk-logs";
import {
  MeterProvider,
  PeriodicExportingMetricReader,
} from "@opentelemetry/sdk-metrics";
import {
  BatchSpanProcessor,
  TraceIdRatioBasedSampler,
  WebTracerProvider,
} from "@opentelemetry/sdk-trace-web";
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
} from "@opentelemetry/semantic-conventions";
import { LOGGING_URL, METRICS_URL, TRACING_URL } from "./telemetryUrls";

// TODO: call logger.setUser() after authentication to get the user id in the logs

const { DEBUG, INFO, WARN, ERROR } = SeverityNumber;
const NAME = `${window.__env__.VITE_APP_NAME}`;
const VERSION = `${window.__env__.VITE_APP_VERSION}`;

const _USER = {
  id: "",
  experiment: "",
};

const resource = resourceFromAttributes({
  [ATTR_SERVICE_NAME]: NAME,
  [ATTR_SERVICE_VERSION]: VERSION,
});

/* Inject W3C trace-context headers on requests to the gateway so a FE span
 * links to its backend span. The app calls the tokenwise gateway at
 * api.ai21.com / api.ai21-stg.com (see VITE_ANALYTICS_API_BASE /
 * VITE_GATEWAY_BASE_URL). All gateway clients use fetch, so the fetch
 * instrumentation must carry this config (the old @ai21/telemetry wrapper
 * only configured XHR, which silently skipped propagation entirely). */
const PROPAGATE_URLS = [/api\.ai21(-stg|-sand)?\.com/];

/* Third-party marketing/analytics traffic is not ours to trace. The sampler is
 * TraceIdRatioBasedSampler(1), so without this every request a GTM tag makes
 * (gtm.js, GA4 /g/collect, ad-platform conversion pings) would export as a
 * 100%-sampled span attributed to this service — unbudgeted volume and noise in
 * a repo that otherwise keeps exactly two FE metrics with locked labels. */
const IGNORE_URLS = [
  /googletagmanager\.com/,
  /google-analytics\.com/,
  /analytics\.google\.com/,
  /* The `hopt` CLI's loopback callback (see auth/deliverCliCredential.ts). The fetch
   * auto-instrumentation has no allowlist, so without this it would export a span for a
   * request whose body is a Firebase refresh token — and spans carry the URL. */
  /^http:\/\/127\.0\.0\.1(:\d+)?\//,
];

registerInstrumentations({
  instrumentations: [
    getWebAutoInstrumentations({
      "@opentelemetry/instrumentation-fetch": {
        propagateTraceHeaderCorsUrls: PROPAGATE_URLS,
        ignoreUrls: IGNORE_URLS,
      },
      "@opentelemetry/instrumentation-xml-http-request": {
        propagateTraceHeaderCorsUrls: PROPAGATE_URLS,
        ignoreUrls: IGNORE_URLS,
      },
    }),
  ],
});

const tracerProvider = new WebTracerProvider({
  sampler: new TraceIdRatioBasedSampler(1),
  resource,
  spanProcessors: TRACING_URL
    ? [new BatchSpanProcessor(new OTLPTraceExporter({ url: TRACING_URL }))]
    : [],
});
tracerProvider.register();

const meterProvider = new MeterProvider({
  resource,
  readers: METRICS_URL
    ? [
        new PeriodicExportingMetricReader({
          exporter: new OTLPMetricExporter({ url: METRICS_URL }),
          exportIntervalMillis: 30 * 1000,
        }),
      ]
    : [],
});

const loggerProvider = new LoggerProvider({
  resource,
  processors: LOGGING_URL
    ? [
        new BatchLogRecordProcessor({
          exporter: new OTLPLogExporter({ url: LOGGING_URL }),
        }),
      ]
    : [],
});

const otelLogger: Logger = loggerProvider.getLogger(NAME);

function emit(
  msg: string,
  level: SeverityNumber,
  attributes: LogAttributes,
  error?: Error,
) {
  otelLogger.emit({
    body: msg,
    severityNumber: level,
    attributes: {
      user: { ..._USER },
      ...(error && {
        "exception.type": error.name,
        "exception.message": error.message,
        "exception.stacktrace": error.stack,
      }),
      ...attributes,
    },
  });
}

function log(
  msg: string,
  level: SeverityNumber,
  attributes: LogAttributes = {},
) {
  level == DEBUG ? console.debug(msg) : console.info(msg);
  emit(msg, level, attributes);
}

function logError(
  error: Error,
  level: SeverityNumber,
  msg = "",
  attributes: LogAttributes = {},
) {
  level == WARN ? console.warn(error) : console.error(error);
  emit(msg || error.message, level, attributes, error);
}

export const logger = {
  debug: (msg: string, attributes?: LogAttributes) =>
    log(msg, DEBUG, attributes),
  info: (msg: string, attributes?: LogAttributes) => log(msg, INFO, attributes),
  warn: (error: Error, msg?: string, attributes?: LogAttributes) =>
    logError(error, WARN, msg, attributes),
  error: (error: Error, msg?: string, attributes?: LogAttributes) =>
    logError(error, ERROR, msg, attributes),
  setUser: (user: { id: string; experiment?: string }) => {
    _USER.id = user.id;
    _USER.experiment = user.experiment ?? "";
  },
};

export const tracer = tracerProvider.getTracer(NAME);
export const meter = meterProvider.getMeter(NAME);

/** Force the batched log processor to export now.
 *
 *  BatchLogRecordProcessor holds records for its scheduled delay, so a log
 *  emitted immediately before a full-page navigation is discarded when the
 *  document goes away — which is exactly the case this app most needs to
 *  record (the departure for a Google sign-in the user never returns from).
 *  Callers about to navigate away should await this first. */
export function flushTelemetry(): Promise<void> {
  return loggerProvider.forceFlush();
}
